import { useEffect, useState } from "react";

interface AppSettings {
  updateEvery: number;
  updatedAt?: string;
  [key: string]: unknown;
}

const STORAGE_KEY = "app-settings";
const DEFAULT_SETTINGS: AppSettings = {
  updateEvery: 60,
};

export const useLocalStorageSettings = () => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);

  // Carrega configurações do localStorage
  const loadSettings = (): AppSettings => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error("Erro ao carregar configurações do localStorage:", error);
    }
    return DEFAULT_SETTINGS;
  };

  // Salva configurações no localStorage
  const saveSettings = async (newSettings: Partial<AppSettings>): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Simula delay para melhor UX
      await new Promise((resolve) => setTimeout(resolve, 300));

      const updatedSettings = {
        ...settings,
        ...newSettings,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSettings));
      setSettings(updatedSettings);

      return true;
    } catch (error) {
      console.error("❌ Erro ao salvar configurações:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Reseta configurações para valores padrão
  const resetSettings = async (): Promise<boolean> => {
    setIsLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      localStorage.removeItem(STORAGE_KEY);
      setSettings(DEFAULT_SETTINGS);

      return true;
    } catch (error) {
      console.error("Erro ao resetar configurações:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega configurações na inicialização
  useEffect(() => {
    const loadedSettings = loadSettings();
    setSettings(loadedSettings);
  }, []);

  return {
    settings,
    saveSettings,
    resetSettings,
    isLoading,
    loadSettings,
  };
};

export default useLocalStorageSettings;
